import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Toast, Spin, Modal, Card, Typography } from '@douyinfe/semi-ui';
import { IconLink, IconEdit, IconPhone, IconQrCode, IconCloud } from '@douyinfe/semi-icons';
import styles from './index.module.scss';
import request from '../../utils/request';
import { IconChangelog } from '@douyinfe/semi-icons-lab';

const Settings = () => {
  const [loading, setLoading] = useState({});
  const [configs, setConfigs] = useState({
    wxpublic_login: '',
    wxpay_js: '',
    qiniu_oss: '',
    wx_url: ''
  });
  const [publicList, setPublicList] = useState([]);
  const [currentModal, setCurrentModal] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState(null);

  const publicTypeMap = {
    1: '微信订阅号',
    2: '微信服务号',
    8: '微信小程序',
    1001: '小红书小程序',
    1002: '抖音小程序'
  };

  const isServiceAccount = (type) => type == 1 || type == 2;
  const isMiniProgram = (type) => type == 8 || type == 1001 || type == 1002;

  const fetchPublicList = async () => {
    try {
      const response = await request.get('/go/spc/weixin/mlist_weixin_public/', {
        params: {
          public_type: 0, // 服务号类型
          key: ''
        }
      });
      if (response.errno === 0) {
        setPublicList(response.list || []);
        // 在获取到列表后初始化 selectedAccountType
        const initValues = configs.wxpublic_login ? JSON.parse(configs.wxpublic_login) : {};
        if (initValues.u) {
          const selectedPublic = response.list.find(item => item.uid === initValues.u);
          if (selectedPublic) {
            setSelectedAccountType(selectedPublic.public_type);
          }
        }
      } else {
        Toast.error(response.errstr || '获取服务号列表失败');
      }
    } catch (error) {
      console.error('获取服务号列表失败:', error);
      Toast.error('获取服务号列表失败');
    }
  };

  useEffect(() => {
    if (currentModal === 'wxpublic_login') {
      fetchPublicList();
    }
  }, [currentModal, configs.wxpublic_login]);

  const [configStatus, setConfigStatus] = useState({});

  useEffect(() => {
    // 从本地缓存加载配置状态
    const cachedStatus = localStorage.getItem('configStatus');
    if (cachedStatus) {
      setConfigStatus(JSON.parse(cachedStatus));
    } else {
      // 当缓存不存在时，获取所有配置的状态
      Object.keys(configs).forEach(key => {
        fetchConfig(key);
      });
    }
  }, []);

  const fetchConfig = async (key) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      const response = await request.get('/go/sp/sp_cfg/get/', { params: { ukey: key } });
      if (response.errno === 0) {
        setConfigs(prev => ({
          ...prev,
          [key]: response.val || ''
        }));

        // 更新配置状态并缓存
        setConfigStatus(prev => {
          const newStatus = {
            ...prev,
            [key]: !!response.val
          };
          localStorage.setItem('configStatus', JSON.stringify(newStatus));
          return newStatus;
        });
      }
    } catch (error) {
      Toast.error('获取配置失败');
      console.error('获取配置失败:', error);
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveConfig = async (key, values) => {
    try {
      const response = await request.post('/go/sp/sp_cfg/set/', {
        ukey: key,
        val: JSON.stringify(values)
      });
      if (response.errno === 0) {
        Toast.success('保存成功');
        // 更新配置状态并缓存
        const newStatus = {
          ...configStatus,
          [key]: true
        };
        setConfigStatus(newStatus);
        localStorage.setItem('configStatus', JSON.stringify(newStatus));
      } else {
        Toast.error(response.errstr || '保存失败');
      }
    } catch (error) {
      Toast.error('保存失败');
      console.error('保存配置失败:', error);
    }
  };

  const wxFormApi = useRef();
  const renderWxPublicForm = () => {
    const initValues = currentModal === 'wxpublic_login' && configs.wxpublic_login ? JSON.parse(configs.wxpublic_login) : {};

    return (
      <Form
        onSubmit={values => {
          // 将天数转换为秒
          const newValues = {
            ...values,
            r: values.r * 24 * 3600
          };
          handleSaveConfig('wxpublic_login', newValues);
        }}
        initValues={initValues}
        getFormApi={formApi => {
          wxFormApi.current = formApi;
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            1. 如果您有一个已认证的服务号，可以直接选择服务号登录，无需用户操作，体验最佳。
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            2. 如果您只有小程序，可以选择小程序扫码登录和支付，需要用户使用微信扫码进行操作。
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            3. 如果都没有，可以选择临时账号登录，系统自动生成一个临时账号，可能存在用户账号丢失或者泄露的风险。
          </Typography.Paragraph>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <Form.Select
              field="u"
              label="选择服务号/小程序"
              placeholder="请选择服务号或小程序"
              rules={[{ required: true, message: '请选择服务号' }]}
              onChange={(value, option) => {
                const selectedPublic = publicList.find(item => item.uid === value);
                if (selectedPublic) {
                  const currentValues = wxFormApi.current.getValues();
                  const newConfig = {
                    u: value,
                    a: selectedPublic.app_id,
                    t: isServiceAccount(selectedPublic.public_type) ? 0 : 1, // 如果是服务号，默认服务号登录；如果是小程序，默认小程序扫码登录
                    p: isServiceAccount(selectedPublic.public_type) ? 0 : 1,  // 如果是服务号，默认微信支付；如果是小程序，默认小程序扫码支付
                    r: currentValues.r, // 保留原有的记住登录时间
                    v: currentValues.v  // 保留原有的小程序版本
                  };
                  setConfigs(prev => ({
                    ...prev,
                    wxpublic_login: JSON.stringify(newConfig)
                  }));
                  setSelectedAccountType(selectedPublic.public_type);
                  wxFormApi.current.setValues(newConfig);
                }
              }}
            >
              {publicList.map(item => (
                <Form.Select.Option
                  key={item.uid}
                  value={item.uid}
                  itemObj={item}
                  showTick
                >
                  {item.public_name} ({publicTypeMap[item.public_type] || '未知类型'})
                </Form.Select.Option>
              ))}
            </Form.Select>
          </div>
          <div style={{ marginTop: '24px' }}>
            <a href="/weixinpublic?public_type=0" target="_blank">公众号管理</a>
          </div>
        </div>
        <Form.Input
          field="a"
          label="AppID"
          placeholder="AppID将根据选择的服务号自动填充"
          disabled
        />
        <Form.InputNumber
          field="r"
          label="记住登录时间(天)"
          placeholder="请输入记住登录的时间（天），0表示不记住"
          min={0}
          initValue={initValues.r ? Math.floor(initValues.r / (24 * 3600)) : 0}
        />
        <Form.Select
          field="t"
          label="登录类型"
          placeholder="请选择登录类型"
          initValue={initValues.t || 0}
          disabled={selectedAccountType === null}
        >
          {isServiceAccount(selectedAccountType) && (
            <Form.Select.Option value={0}>服务号登录</Form.Select.Option>
          )}
          {isServiceAccount(selectedAccountType) && (
            <Form.Select.Option value={2}>临时账号登录</Form.Select.Option>
          )}
          {isMiniProgram(selectedAccountType) && (
            <Form.Select.Option value={1}>小程序扫码登录</Form.Select.Option>
          )}
          {isMiniProgram(selectedAccountType) && (
            <Form.Select.Option value={2}>临时账号登录</Form.Select.Option>
          )}
        </Form.Select>
        <Form.Select
          field="p"
          label="支付类型"
          placeholder="请选择支付类型"
          initValue={initValues.p || 0}
          disabled={selectedAccountType === null}
        >
          {isServiceAccount(selectedAccountType) && (
            <Form.Select.Option value={0}>微信支付</Form.Select.Option>
          )}
          {isMiniProgram(selectedAccountType) && (
            <Form.Select.Option value={1}>小程序扫码支付</Form.Select.Option>
          )}
        </Form.Select>
        <Form.Select
          field="v"
          label="小程序版本(上线后需选择正式版)"
          placeholder="请选择小程序版本"
          initValue={initValues.v || 'release'}
          disabled={!isMiniProgram(selectedAccountType)}
        >
          <Form.Select.Option value="release">正式版</Form.Select.Option>
          <Form.Select.Option value="develop">开发版</Form.Select.Option>
          <Form.Select.Option value="trial">体验版</Form.Select.Option>
        </Form.Select>
        <Button htmlType="submit" type="primary">保存</Button>
      </Form>
    );
  };

  const renderWxPayForm = (type) => {
    const key = type === 'js' ? 'wxpay_js' : 'wxpay_xcx';
    const title = type === 'js' ? 'JSAPI支付' : '小程序支付';
    const initValues = currentModal === key && configs[key] ? JSON.parse(configs[key]) : {};
    return (
      <Form
        onSubmit={values => handleSaveConfig(key, values)}
        initValues={initValues}
      >
        <Form.Input
          field="a"
          label="AppID"
          placeholder="请输入AppID"
          rules={[{ required: true, message: '请输入AppID' }]}
        />
        <Form.Input
          field="m"
          label="商户号"
          placeholder="请输入商户号"
          rules={[{ required: true, message: '请输入商户号' }]}
        />
        <Form.Input
          field="k"
          label="API V3密钥"
          placeholder="请输入API V3密钥"
          rules={[{ required: true, message: '请输入API V3密钥' }]}
        />
        <Form.Input
          field="s"
          label="证书序列号"
          placeholder="请输入证书序列号"
          rules={[{ required: true, message: '请输入证书序列号' }]}
        />
        <Form.TextArea
          field="i"
          label="商户私钥"
          placeholder="请输入商户私钥内容"
          rules={[{ required: true, message: '请输入商户私钥内容' }]}
        />
        <Button htmlType="submit" type="primary">保存</Button>
      </Form>
    );
  };

  const renderQiniuForm = () => {
    const initValues = currentModal === 'qiniu_oss' && configs.qiniu_oss ? JSON.parse(configs.qiniu_oss) : {};
    return (
      <Form
        onSubmit={values => handleSaveConfig('qiniu_oss', values)}
        initValues={initValues}
      >
        <Form.Input
          field="a"
          label="AccessKey"
          placeholder="请输入AccessKey"
          rules={[{ required: true, message: '请输入AccessKey' }]}
        />
        <Form.Input
          field="s"
          label="SecretKey"
          placeholder="请输入SecretKey"
          rules={[{ required: true, message: '请输入SecretKey' }]}
        />
        <Form.Input
          field="b"
          label="存储空间"
          placeholder="请输入存储空间名称"
          rules={[{ required: true, message: '请输入存储空间名称' }]}
        />
        <Form.Input
          field="d"
          label="访问域名"
          placeholder="请输入完整的访问域名，如https://example.com/"
          rules={[{
            required: true,
            message: '请输入访问域名'
          }, {
            validator: (rule, value) => {
              if (!value) return true;
              return /^https?:\/\/.+\/$/.test(value);
            },
            message: '域名必须以http://或https://开头，并以/结尾'
          }]}
        />
        <Button htmlType="submit" type="primary">保存</Button>
      </Form>
    );
  };


  const renderWxUrlForm = () => {
    const initValues = currentModal === 'wx_url' && configs.wx_url ? JSON.parse(configs.wx_url) : {};
    return (
      <Form
        onSubmit={values => handleSaveConfig('wx_url', values)}
        initValues={initValues}
      >
        <div style={{ marginBottom: 24 }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            需要与微信公众平台和微信支付后台填写的域名保持一直
          </Typography.Paragraph>
        </div>
        <Form.Input
          field="j"
          label="网页授权域名"
          placeholder="请输入网页授权域名，如 weixin.uctphp.com"
          rules={[{ required: true, message: '请输入网页授权域名' }]}
        />
        <Form.Input
          field="p"
          label="JS支付授权域名"
          placeholder="请输入JS支付授权域名，如 https://weixin.uctphp.com/"
          rules={[{
            required: true,
            message: '请输入JS支付授权域名'
          }, {
            validator: (rule, value) => {
              if (!value) return true;
              return /^https?:\/\/.+\/$/.test(value);
            },
            message: '域名必须以http://或https://开头，并以/结尾'
          }]}
        />
        <Button htmlType="submit" type="primary">保存</Button>
      </Form>
    );
  };

  const getConfigIcon = (configKey) => {
    switch (configKey) {
      case 'wxpublic_login':
      case 'wxpay_js':
        return <IconPhone size="large" style={{ marginRight: 8 }} />;
      case 'wx_url':
        return <IconLink size="large" style={{ marginRight: 8 }} />;
      case 'wxpay_xcx':
        return <IconQrCode size="large" style={{ marginRight: 8 }} />;
      case 'qiniu_oss':
        return <IconCloud size="large" style={{ marginRight: 8 }} />;
      default:
        return null;
    }
  };

  const renderConfigCard = (title, configKey) => {
    const config = configs[configKey];
    const configData = config ? JSON.parse(config) : {};
    const isLoading = loading[configKey];
    const isConfigured = configStatus[configKey];

    const handleEdit = async () => {
      setLoading(prev => ({ ...prev, [configKey]: true }));
      try {
        const response = await request.get('/go/sp/sp_cfg/get/', { params: { ukey: configKey } });
        if (response.errno === 0) {
          setConfigs(prev => ({
            ...prev,
            [configKey]: response.val || ''
          }));

          // 更新配置状态并缓存
          const newStatus = {
            ...configStatus,
            [configKey]: !!response.val
          };
          setConfigStatus(newStatus);
          localStorage.setItem('configStatus', JSON.stringify(newStatus));
          setCurrentModal(configKey);
        } else {
          setCurrentModal(configKey);
        }
      } catch (error) {
        Toast.error('获取配置失败');
        console.error('获取配置失败:', error);
      } finally {
        setLoading(prev => ({ ...prev, [configKey]: false }));
      }
    };

    return (
      <Card className={styles.settingItem}>
        <div className={styles.cardHeader}>
          <h3>{getConfigIcon(configKey)}{title}</h3>
          <Button
            theme="light"
            type="primary"
            icon={<IconEdit />}
            onClick={handleEdit}
            loading={isLoading}
          >
            编辑
          </Button>
        </div>
        <div className={styles.cardContent}>
          <div className={styles.configStatus}>
            {isConfigured ? (
              <span className={styles.configured}>已配置</span>
            ) : (
              <span className={styles.unconfigured}>未配置</span>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className={styles.settingsContent}>
      <h2>系统设置</h2>
      <div style={{ marginBottom: 16, color: 'var(--semi-color-text-2)', fontSize: 12 }}>
        不知道如何填写相关参数，请查看 <a href="https://doc.weixin.qq.com/doc/w3_AIgAcwamAMA5xC0O7FET7q4DBymqr" target="_blank" rel="noopener noreferrer"><IconChangelog size="small"></IconChangelog>帮助文档</a>
      </div>

      <Spin spinning={false}>
        <div className={styles.settingsList}>
          {renderConfigCard('网页登录配置', 'wxpublic_login')}
          {renderConfigCard('微信域名配置', 'wx_url')}
          {renderConfigCard('微信支付配置', 'wxpay_js')}
          {renderConfigCard('小程序支付配置', 'wxpay_xcx')}
          {renderConfigCard('七牛云存储配置', 'qiniu_oss')}
        </div>
      </Spin>

      <Modal
        title={{
          'wxpublic_login': '网页登录配置',
          'wxpay_js': '微信支付配置',
          'wxpay_xcx': '小程序支付配置',
          'qiniu_oss': '七牛云存储配置',
          'wx_url': '微信域名配置'
        }[currentModal]}
        visible={!!currentModal}
        onCancel={() => setCurrentModal(null)}
        footer={null}
        size="large"
      >
        {currentModal === 'wxpublic_login' && renderWxPublicForm()}
        {currentModal === 'wxpay_js' && renderWxPayForm('js')}
        {currentModal === 'wxpay_xcx' && renderWxPayForm('xcx')}
        {currentModal === 'qiniu_oss' && renderQiniuForm()}
        {currentModal === 'wx_url' && renderWxUrlForm()}
      </Modal>
    </div>
  );
};

export default Settings;